import React, { useEffect, useMemo, useState } from 'react';
import { Popover, Checkbox, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import GroupChangeSummary from './GroupChangeSummary';
import { getMemberGroups, getMemberGroupsOfMember, createMemberGroup, addMemberGroupMembers } from '../../../../api/member-group';
import { errorMessages } from '../../../../utils/methods';
import {
  PopoverPanel,
  PopoverTitle,
  PopoverScrollArea,
  PopoverGroupRow,
  PopoverGroupName,
  PopoverGroupCount,
  PopoverJoinedMark,
  PopoverDivider,
  PopoverNewGroupButton,
  PopoverFooter,
  PopoverCancelButton,
  PopoverSubmitButton,
  PopoverEmptyText,
  InlineInputWrap,
  InlineInput,
  InlineInputAction,
} from './MemberGroupModalStyled';

/**
 * 회원목록에서 체크한 회원을 그룹에 담는 팝오버.
 * 여러 그룹을 한 번에 고를 수 있고, 그룹마다 순차로 추가 API를 보낸다.
 */
const GroupAddPopover = ({ open, anchorEl, onClose, selectedMembers = [], branchId: branchIdProp }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const queryClient = useQueryClient();
  // 회원 상세는 화면에서 고른 지점이 로그인 지점과 다를 수 있어 지점을 넘겨받는다.
  const branchId = branchIdProp ?? user?.branch?.branchId;

  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPending, setCreatePending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 그룹 관리 모달과 같은 쿼리 키를 써서 한쪽에서 만들거나 지운 그룹이 즉시 반영되게 한다.
  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['memberGroups', branchId],
    queryFn: () => getMemberGroups(user.token, branchId),
    enabled: open && !!branchId && !!user?.token,
  });

  /*
   * 한 명만 골랐을 때는 그 회원이 이미 어느 그룹에 있는지 보여 준다. 모르면 이미 담긴 그룹을
   * 또 골라 '이미 모두 소속' 안내만 보게 된다. 여러 명이면 회원마다 소속이 달라 한 줄로 못 적는다.
   */
  const singleMemberId = selectedMembers.length === 1 ? selectedMembers[0]?.memberId : null;
  const { data: joinedGroups = [] } = useQuery({
    queryKey: ['memberGroupsOfMember', singleMemberId, branchId],
    queryFn: () => getMemberGroupsOfMember(user.token, singleMemberId, branchId),
    enabled: open && !!singleMemberId && !!branchId && !!user?.token,
  });
  const joinedIds = useMemo(
    () => new Set((joinedGroups || []).map(group => group.id)),
    [joinedGroups],
  );

  useEffect(() => {
    if (open) return;
    setCheckedIds(new Set());
    setCreating(false);
    setCreateName('');
  }, [open]);

  const toggleGroup = groupId =>
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });

  // 생성 API는 메시지만 돌려준다. 목록을 다시 받아 같은 이름의 그룹을 찾아 체크해 준다.
  // 전역 staleTime 이 30초라 fetchQuery 는 캐시를 그대로 돌려준다(방금 만든 그룹이 안 보임).
  // 조회 함수를 직접 부르고 그 결과를 캐시에 넣어 그룹 관리 모달과도 같은 목록을 보게 한다.
  const handleCreate = async () => {
    const name = createName.trim();
    if (!name || createPending) return;
    setCreatePending(true);
    try {
      await createMemberGroup(user.token, { branchId, name });
      const fresh = await getMemberGroups(user.token, branchId);
      queryClient.setQueryData(['memberGroups', branchId], fresh);
      const created = (fresh || []).find(group => group.name === name);
      if (created) setCheckedIds(prev => new Set(prev).add(created.id));
      setCreating(false);
      setCreateName('');
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setCreatePending(false);
    }
  };

  /*
   * 여러 그룹에 한 번에 담을 수 있어서, 어느 그룹이 몇 명이 되는지 미리 보여준다 (#78).
   * 이미 소속된 회원은 서버가 걸러내므로 여기 숫자는 '최대'다.
   */
  const [confirmOpen, setConfirmOpen] = useState(false);

  const changeRows = useMemo(() => {
    const memberCount = selectedMembers.filter(member => member.memberId).length;
    return groups
      .filter(group => checkedIds.has(group.id))
      .map(group => {
        const before = group.memberCount ?? 0;
        return {
          label: group.name,
          before: `${before}명`,
          after: `${before + memberCount}명`,
          tone: memberCount > 0 ? 'up' : undefined,
        };
      });
  }, [groups, checkedIds, selectedMembers]);

  const handleSubmit = async () => {
    const memberIds = selectedMembers.map(member => member.memberId).filter(Boolean);
    const targets = groups.filter(group => checkedIds.has(group.id));
    if (!memberIds.length || !targets.length || submitting) return;

    setSubmitting(true);
    let addedTotal = 0;
    let skippedTotal = 0;
    const failed = [];
    // 그룹별로 나눠 보낸다. 한 그룹이 실패해도 나머지는 계속 진행한다.
    for (const group of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await addMemberGroupMembers(user.token, group.id, memberIds);
        addedTotal += result?.addedCount ?? 0;
        skippedTotal += result?.skippedCount ?? 0;
      } catch (error) {
        failed.push(`${group.name} (${errorMessages(error)})`);
      }
    }
    setSubmitting(false);

    if (failed.length) toast.error(`${t('추가 실패')} — ${failed.join(', ')}`);
    if (addedTotal > 0) {
      // 그룹이 여러 개면 addedTotal 은 '회원 수'가 아니라 '그룹×회원' 건수다. 단위를 나눠 적는다.
      toast.success(
        targets.length > 1
          ? `${targets.length}${t('개 그룹에')} ${memberIds.length}${t('명 담기 완료')}${
              skippedTotal > 0 ? ` (${t('이미 소속')} ${skippedTotal}${t('건 제외')})` : ''
            }`
          : skippedTotal > 0
            ? `${targets[0].name} ${addedTotal}${t('명 추가 (이미 소속된')} ${skippedTotal}${t('명 제외)')}`
            : `${targets[0].name} ${addedTotal}${t('명이 추가되었습니다')}`,
      );
    } else if (!failed.length) {
      toast.info(t('이미 모두 그룹에 소속된 회원입니다'));
    }

    queryClient.invalidateQueries({ queryKey: ['memberGroups', branchId] });
    queryClient.invalidateQueries({ queryKey: ['memberGroupMembers'] });
    // 회원 상세의 소속 칩도 같은 담기로 바뀐다.
    queryClient.invalidateQueries({ queryKey: ['memberGroupsOfMember'] });
    // 그룹 관리 모달의 표도 같은 데이터를 본다. 빼먹으면 회원수만 늘고 행은 그대로다.
    queryClient.invalidateQueries({ queryKey: ['memberGroupMemberRows'] });
    // 전부 실패했으면 체크한 그룹을 남겨 둔다. 닫으면 초기화돼서 처음부터 다시 골라야 한다.
    if (addedTotal > 0 || !failed.length) onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      slotProps={{ paper: { sx: { marginTop: '-10px', borderRadius: '10px', overflow: 'visible' } } }}>
      <PopoverPanel>
        <PopoverTitle>{t('그룹에 담기')}</PopoverTitle>

        <PopoverScrollArea>
          {isLoading ? (
            <PopoverEmptyText>
              <CircularProgress size={18} sx={{ color: '#4AB3BC' }} />
            </PopoverEmptyText>
          ) : groups.length === 0 ? (
            <PopoverEmptyText>
              {t('아직 그룹이 없습니다')}
              <br />
              {t('아래에서 새 그룹을 만들어 주세요')}
            </PopoverEmptyText>
          ) : (
            groups.map(group => {
              const joined = joinedIds.has(group.id);
              return (
                <PopoverGroupRow
                  key={group.id}
                  onClick={() => !joined && toggleGroup(group.id)}
                  sx={joined ? { cursor: 'default', opacity: 0.75 } : null}>
                  <Checkbox
                    size="small"
                    checked={joined || checkedIds.has(group.id)}
                    disabled={joined}
                    sx={{ padding: 0, color: '#BBB', '&.Mui-checked': { color: '#4AB3BC' } }}
                  />
                  <PopoverGroupName>{group.name}</PopoverGroupName>
                  {joined && <PopoverJoinedMark>{t('소속')}</PopoverJoinedMark>}
                  <PopoverGroupCount>
                    {group.memberCount}
                    {t('명')}
                  </PopoverGroupCount>
                </PopoverGroupRow>
              );
            })
          )}
        </PopoverScrollArea>

        <PopoverDivider />

        {creating ? (
          <InlineInputWrap>
            <InlineInput
              autoFocus
              placeholder={t('그룹 이름')}
              value={createName}
              inputProps={{ maxLength: 50 }}
              onChange={e => setCreateName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') {
                  // 막지 않으면 Popover 까지 Escape 가 올라가 팝오버가 통째로 닫히고 체크가 날아간다.
                  e.stopPropagation();
                  setCreating(false);
                }
              }}
            />
            <InlineInputAction className="confirm" onClick={handleCreate}>
              {createPending ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 17 }} />}
            </InlineInputAction>
            <InlineInputAction onClick={() => setCreating(false)}>
              <CloseIcon sx={{ fontSize: 17 }} />
            </InlineInputAction>
          </InlineInputWrap>
        ) : (
          <PopoverNewGroupButton onClick={() => setCreating(true)}>
            <AddIcon sx={{ fontSize: 16 }} />
            {t('새 그룹 만들기')}
          </PopoverNewGroupButton>
        )}

        <PopoverFooter>
          <PopoverCancelButton onClick={onClose}>{t('취소')}</PopoverCancelButton>
          <PopoverSubmitButton
            disabled={checkedIds.size === 0 || submitting}
            onClick={() => checkedIds.size > 0 && !submitting && setConfirmOpen(true)}>
            {submitting ? (
              <CircularProgress size={14} sx={{ color: '#fff' }} />
            ) : (
              `${selectedMembers.length}${t('명 담기')}`
            )}
          </PopoverSubmitButton>
        </PopoverFooter>
      </PopoverPanel>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        onConfirm={() => {
          if (submitting) return;
          setConfirmOpen(false);
          handleSubmit();
        }}
        message={`선택한 ${selectedMembers.length}명을 담습니다.`}
        highlightText={`${selectedMembers.length}명`}
        highlightColor="#0F7F85"
        cancelText="취소"
        confirmText="담기"
        confirmColor="#0F7F85"
        detail={(
          <GroupChangeSummary rows={changeRows} />
        )}
      />
    </Popover>
  );
};

export default GroupAddPopover;
